import React from "react";
import Nav from "@/components/Nav";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ArrowRight, CreditCard, FileText, Building2, Scale } from "lucide-react";

function Step({ n, title, body }) {
  return (
    <div className="flex gap-4">
      <div className="shrink-0 w-10 h-10 rounded-full bg-[#EEF4FF] text-[#1E3A8A] font-bold grid place-items-center">{n}</div>
      <div>
        <h3 className="font-semibold text-slate-900 text-lg">{title}</h3>
        <div className="text-slate-600 mt-2 text-sm leading-relaxed">{body}</div>
      </div>
    </div>
  );
}

const CB_TEMPLATE = `To my card issuer,

I am filing a dispute for the following charge on my account:

- Card ending in: [LAST 4]
- Merchant: [MERCHANT NAME]
- Charge date: [DATE]
- Amount: $[AMOUNT]
- Reason: Unauthorized recurring charge — I cancelled this subscription on [CANCEL DATE] and was charged after cancellation.

I attempted to resolve this directly with the merchant on the following dates: [DATE 1], [DATE 2], [DATE 3], [DATE 4]. I did not receive a satisfactory resolution.

I am requesting a chargeback under Regulation E / Reg Z as an unauthorized transaction.

Attached: cancellation confirmation, prior support correspondence, and the charge statement.

Thank you,
[YOUR NAME]`;

export default function ChargebackGuide() {
  return (
    <div className="min-h-screen bg-[#FAFBFD]">
      <Nav />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold mb-4" data-testid="chargeback-guide-badge">
          <AlertTriangle className="w-3.5 h-3.5" /> Last-resort playbook
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight">Chargeback guide</h1>
        <p className="text-slate-600 mt-3 max-w-2xl">You've sent all 4 messages and still got nothing. This is the path most people don't know exists — filing a chargeback with your bank.</p>

        <Card className="trust-card mt-8">
          <CardContent className="p-6 sm:p-8 space-y-6">
            <Step n="1" title="Gather your evidence" body={
              <>You'll need three things: (a) proof you cancelled (cancellation email or in-app screenshot), (b) proof of the charge (bank statement or card app screenshot showing date + amount), (c) proof you tried to resolve directly (all 4 messages from your case page — long-press to save each one).</>
            } />
            <Step n="2" title="Call your card issuer" body={
              <>Call the number on the back of your card. Say the words: <em>"I want to file a dispute for an unauthorized recurring charge."</em> Do not say "I want a refund" — the language matters. Most banks (Chase, Amex, Capital One, Discover, Citi) also let you file the dispute directly in the app under the charge itself.</>
            } />
            <Step n="3" title="Send the letter (below)" body={
              <>Some banks require a written statement. Copy the template below, fill in your details, and email/upload it to your dispute case. Attach your evidence. Keep the tone factual — no threats, no emotion.</>
            } />
            <Step n="4" title="Wait 30–90 days" body={
              <>The bank will issue a provisional credit within 10 business days for most disputes. Then they investigate. If the merchant doesn't respond or their evidence is weak, the credit becomes permanent. If they win, you'll get a letter — you can appeal once.</>
            } />
          </CardContent>
        </Card>

        <Card className="trust-card mt-6">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-5 h-5 text-[#1E3A8A]" />
              <h2 className="text-lg font-semibold text-slate-900">Chargeback letter template</h2>
            </div>
            <p className="text-sm text-slate-500 mb-4">Copy, fill in the bracketed fields, and submit to your card issuer.</p>
            <pre className="whitespace-pre-wrap font-sans text-sm bg-slate-50 border border-slate-200 rounded-xl p-4 text-slate-700" data-testid="chargeback-template-body">{CB_TEMPLATE}</pre>
            <Button
              onClick={() => { navigator.clipboard.writeText(CB_TEMPLATE).catch(()=>{}); }}
              className="mt-4 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF]"
              data-testid="chargeback-copy-template-button"
            >
              Copy template
            </Button>
          </CardContent>
        </Card>

        <div className="grid sm:grid-cols-3 gap-3 mt-6">
          <div className="trust-card p-4 flex items-start gap-3">
            <CreditCard className="w-5 h-5 text-slate-500 mt-0.5" />
            <div>
              <p className="font-semibold text-sm text-slate-900">Time limit</p>
              <p className="text-xs text-slate-500 mt-1">You have 60 days from the statement date (US law).</p>
            </div>
          </div>
          <div className="trust-card p-4 flex items-start gap-3">
            <Building2 className="w-5 h-5 text-slate-500 mt-0.5" />
            <div>
              <p className="font-semibold text-sm text-slate-900">BBB + Attorney General</p>
              <p className="text-xs text-slate-500 mt-1">File complaints in parallel — free, and merchants often settle to close them.</p>
            </div>
          </div>
          <div className="trust-card p-4 flex items-start gap-3">
            <Scale className="w-5 h-5 text-slate-500 mt-0.5" />
            <div>
              <p className="font-semibold text-sm text-slate-900">Small claims</p>
              <p className="text-xs text-slate-500 mt-1">Only worth it for amounts &gt;$300. File in the state your merchant does business in.</p>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link to="/dashboard"><Button variant="outline" className="rounded-full">Back to my cases <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
        </div>

        <p className="text-xs text-slate-400 text-center mt-8 max-w-lg mx-auto">
          Powerhouse Refunds is not a law firm. This guide is general information, not legal advice. For amounts over $500 or complex disputes, consider a consumer protection attorney.
        </p>
      </div>
    </div>
  );
}
